import React, { useEffect, useMemo, useState } from 'react';
import {
  Image,
  ImageBackground,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import DriverCard from '../components/booking/DriverCard';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import Icon, { IconName } from '../components/ui/Icon';
import Text from '../components/ui/Text';
/* `bookedServiceNames` and `bookingsFor` read the bundled examples. This screen
   deliberately imports neither - see the note on `Props.bookings`. */
import {
  Booking,
  BookingFilter,
  BookingStatus,
  bookingFilters,
  DEFAULT_FILTER,
} from '../data/bookings';
import {
  categories as seedCategories,
  Category,
  CategoryKey,
  Service,
  services as seedCatalogue,
} from '../data/services';
import { canReschedule } from '../data/cancellation';
import { jobProgress } from '../data/progress';
import { refundFor, refundLine } from '../data/refunds';
import { openWhatsappSupport } from '../services/support';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/**
 * The banner's own width over its height.
 *
 * `final.png` is exactly 2:1. Written as the measured dimensions rather than
 * as `2` so that resupplying the artwork is a matter of putting its real size
 * here, and so the number is checkable against the file.
 *
 * The plate is a background now rather than the whole banner - the words are
 * drawn over it (see the render) - so a crop no longer costs a headline. It is
 * still driven by the aspect so the vase and the framed print stay in frame on
 * a narrow phone.
 */
const BANNER_ASPECT = 1774 / 887;

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');


/**
 * How each status is marked, so a card says what it is before it is read.
 *
 * Filled tints rather than outlines. An outline in the status colour asked the
 * eye to find a hairline to tell a live booking from a cancelled one; a filled
 * chip carries the same colour across its whole area and reads at a glance
 * down a scrolling list, which is the only way this label is ever read.
 */
const TONE: Record<
  BookingStatus,
  { label: string; tint: string; fill: string; icon: IconName }
> = {
  live: {
    label: 'LIVE',
    tint: colors.onPrimary,
    fill: colors.primary,
    icon: 'clock',
  },
  recurring: {
    label: 'REPEATS',
    tint: colors.primary,
    fill: colors.surfaceAccent,
    icon: 'clock',
  },
  completed: {
    label: 'DONE',
    tint: colors.inkMuted,
    fill: colors.surfaceMuted,
    icon: 'check',
  },
  cancelled: {
    label: 'CANCELLED',
    tint: colors.danger,
    fill: colors.dangerSurface,
    icon: 'minus',
  },
};

type Props = {
  /**
   * The customer's bookings, from the platform.
   *
   * This screen does not own the list and has no source of its own: it draws
   * whichever one it is handed, and an empty one draws the empty state. It
   * must never reach for `data/bookings` - those are somebody else's.
   */
  bookings?: Booking[];
  /** True while the list is being (re)fetched, for the pull-to-refresh. */
  loading?: boolean;
  /** Set when the last fetch failed. Shown instead of an empty list. */
  error?: string | null;
  onRefresh?: () => void;
  /**
   * The live catalogue, for the "book again" rail.
   *
   * Passed in for the same reason the list is: the bundled `services` array is
   * an offline fallback now, and a rail built from it offers services the
   * platform may not sell - at prices nobody set.
   */
  services?: Service[];
  categoryFor?: (key: CategoryKey) => Category;
  /** Opens the catalogue, for someone with nothing booked yet. */
  onBrowse: () => void;
  /** Opens one booking in full. */
  onOpenBooking?: (booking: Booking) => void;
  /** Opens a service they have booked before, to book it again. */
  onRebook?: (service: Service) => void;
  /**
   * Opens the slot picker to move a booking that has not started yet.
   *
   * The same handler the detail screen's "Reschedule this booking" uses, so
   * both entry points open one picker and settle through one code path. The
   * card decides whether to offer it with `canReschedule`, not by status:
   * a live booking whose professional is already on the way has nothing left
   * to move, and the stage is what knows that.
   */
  onReschedule?: (booking: Booking) => void;
  /** Opens a whole trade - every subcategory under it, not one service. */
  onSelectCategory?: (key: CategoryKey) => void;
  /**
   * Opens the safety screen for the live trip, if there is one.
   *
   * Threaded through rather than handled here because `SosScreen` is a
   * full-screen page owned by `HomeScreen`, exactly like the booking-side
   * `onSos`. A customer ride has no detail screen of its own, so the driver
   * card at the top of this list is the only place the alarm can be reached
   * from.
   */
  onRideSos?: (ride: {
    id: string;
    driverName?: string | null;
    plate?: string | null;
  }) => void;
};

/**
 * Everything already booked.
 *
 * Grouped by status rather than sorted by date. "Is anyone coming today" and
 * "what did I pay in May" are different questions, and a single reverse-
 * chronological list answers the second while burying the first - a live
 * booking three rows down is a live booking nobody sees.
 *
 * ---------------------------------------------------------------------------
 * MOST OF THIS IS NOT REAL.
 * ---------------------------------------------------------------------------
 * The list comes from the platform, through `useBookings`, and is handed in as
 * a prop. Nothing on this screen reads `src/data/bookings.ts` - that file is
 * example data for isolated renders, and showing it to a real customer would
 * be showing them somebody else's jobs.
 */
function BookingsScreen({
  bookings = [],
  loading = false,
  error = null,
  onRefresh,
  services,
  categoryFor,
  onBrowse,
  onOpenBooking,
  onRebook,
  onReschedule,
  onSelectCategory,
  onRideSos,
}: Props) {
  const resp = useResponsive();
  const [filter, setFilter] = useState<BookingFilter>(DEFAULT_FILTER);

  /*
   * A heartbeat, so the live countdowns on the cards actually move.
   *
   * The value is never read - `jobProgress` reads the real clock itself, and
   * this exists only to re-render the list once a minute so those readings
   * are retaken. A minute rather than a second because the cards quote
   * minutes ("about 40 mins left"): ticking every second would re-render the
   * whole list sixty times to change nothing on screen.
   *
   * The interval is only scheduled while something is actually being worked
   * on, so a list of finished bookings costs nothing.
   */
  const anyRunning = bookings.some(booking => booking.stage === 'service');
  const [, setNowTick] = useState(0);

  useEffect(() => {
    if (!anyRunning) {
      return;
    }
    const id = setInterval(() => setNowTick(tick => tick + 1), 60 * 1000);
    return () => clearInterval(id);
  }, [anyRunning]);

  /*
   * The banner, measured in points rather than left to `aspectRatio`.
   *
   * An `Image` given `width: undefined` and an aspect ratio does not shrink to
   * its parent - it falls back to the file's intrinsic width, 1672px, and drew
   * a banner five screens wide. Both dimensions are computed here instead, off
   * the page column minus the page's own margins - the column rather than the
   * window, because on a tablet this page is a centred 560pt column and a
   * banner measured off a 1024pt screen would hang off both sides of it.
   */
  const bannerWidth = resp.contentWidth - screenPadding * 2;
  const bannerHeight = Math.round(bannerWidth / BANNER_ASPECT);

  /*
   * Every booking on file, whatever the filter is showing.
   *
   * Summed across the filters rather than read off a list: the seed data is
   * exposed as `bookingsFor(status)`, so there is no single array to measure -
   * and the filter is a lens on the whole set, not a different set.
   *
   * Computed plainly rather than behind `useMemo`: the arrays involved are a
   * handful of items, so memoising buys nothing, and a cancelled booking is
   * `seededBookings.find(...)` flipped in place - see `HomeScreen`'s
   * `cancelBooking` - which changes what `bookingsFor` returns without
   * changing `placed`, the only thing a memo here would have watched. A
   * cached `total`/`shown` would keep counting and listing it under its old
   * filter until something else happened to change `filter` or `placed`.
   */
  /*
   * The customer's list, and only ever theirs.
   *
   * There is deliberately NO fallback to the bundled examples. This used to
   * treat `undefined` as "no source wired in" and draw the seed data, which
   * meant a signed-out guest was shown a stranger's booking - a professional's
   * name and somebody else's address - as their own history. An empty list is
   * a real answer, and it is the right one for anyone who has booked nothing.
   */
  const total = bookings.length;
  const shown = bookings.filter(booking => booking.status === filter);

  /*
   * The rail, grouped by trade rather than one card per service.
   *
   * Ten separate cards was a second catalogue - a long strip of near-identical
   * tiles you have to read to tell apart. Grouped, it is three or four cards
   * that answer "what do I use this app for", and the count carries the rest.
   *
   * A name with no catalogue match is dropped rather than given a card of its
   * own: the group is keyed on the service's category, and a service that no
   * longer exists has none. The list is history, so this can only happen if the
   * catalogue drops something that was booked.
   */
  const booked = useMemo(() => {
    const groups: Array<{ category: Category; services: Service[] }> = [];
    const catalogue = services ?? seedCatalogue;
    const lookup =
      categoryFor ?? ((key: CategoryKey) => seedCategories.find(c => c.key === key));

    /*
     * What THIS customer has booked, and nothing else.
     *
     * There is no fallback to `bookedServiceNames()` - the bundled examples -
     * because "book again" is a claim about the reader's own history. Offered
     * to a guest it invited them to repeat a job they had never had done. An
     * empty rail is the honest answer for someone with no history.
     *
     * Matched by `serviceId` first: names differ between the two catalogues,
     * and matching on them dropped every real booking off this rail silently.
     */
    const names = bookings.map(item => ({
      id: item.serviceId,
      name: item.title,
    }));

    names.forEach(({ id, name }) => {
      const service =
        (id ? catalogue.find(item => item.key === id) : undefined) ??
        catalogue.find(item => item.name === name);
      if (!service) {
        return;
      }
      const existing = groups.find(
        group => group.category.key === service.category,
      );
      if (existing) {
        /* History repeats - the same service booked twice is one tile. */
        if (!existing.services.some(item => item.key === service.key)) {
          existing.services.push(service);
        }
        return;
      }
      const category = lookup(service.category);
      if (category) {
        groups.push({ category, services: [service] });
      }
    });

    return groups;
  }, [bookings, categoryFor, services]);

  return (
    <View style={styles.root}>
      <ScrollView
        /* `column` is zero on every phone; on a tablet it narrows the page to
           a readable column and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.column]}
        showsVerticalScrollIndicator={false}
        /*
          Pull to refresh, which is the app's substitute for being told.
          A live booking is the one screen where stale data actively misleads -
          a professional gets assigned, sets off and arrives, and none of it
          reaches the phone until somebody asks. Push notifications are the
          real answer; this is what exists today, and it is why the gesture is
          on this screen and not on the catalogue.
        */
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={loading}
              onRefresh={onRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          ) : undefined
        }
      >
        {/*
          The screen's own title bar: what the page is, and how much of it
          there is.

          The count moved out from under the title and onto the right. As a
          subtitle it was a second line of type competing with the heading; as
          a chip opposite it, it is a number you read at a glance and one line
          of header instead of two.
        */}
        <View style={styles.titleBar}>
          {/* The house-and-ribbon mark, at the type's own size rather than the
              24pt grid - an icon taller than the letters beside it reads as a
              button someone forgot to make pressable. */}
          <Icon name="homeBookmark" size={22} color={colors.primary} />

          <Text variant="titleLg" style={styles.title}>
            My Bookings
          </Text>

          <View style={styles.count}>
            <Icon name="calendar" size={14} color={colors.primary} />
            <Text variant="labelSm" color={colors.primary}>
              {total === 0
                ? 'None yet'
                : `${total} ${total === 1 ? 'booking' : 'bookings'}`}
            </Text>
          </View>
        </View>

        {/*
          The live ride, above everything else on the page.

          A customer ride is a standalone `customer_trip` with no `bookingId`,
          so it can never appear in the list below and has no detail screen of
          its own to sit on. This tab is where somebody comes to find out what
          is happening right now, which makes it the only honest home for it -
          and "right now" belongs at the top, above a rail of things they
          booked last month.

          The card fetches its own ride and draws nothing at all when there is
          not one, so this screen keeps its rule of owning no data. See
          `DriverCard` for why that exception was made.
        */}
        <DriverCard onSos={onRideSos} />

        {/* --------------------------------------------------- what they use */}
        <View style={styles.rail}>
          <Text variant="titleLg" style={styles.railTitle}>
            Book again
          </Text>

          {booked.length ? (
            /*
              One row, swiped - not a wrapping grid.

              `CategoryGrid` is the wrong shape for this block, and its own
              header says so: it is a grid on purpose, because Home's eight
              categories are the spine of the app and all of them have to be
              visible without a gesture. This row is the opposite kind of list.
              It is history, it has no fixed length, and it grows the more of
              the app you use - which is exactly the case a grid handles worst.
              At three across a fourth trade dropped to a second row and pushed
              the bookings themselves off the screen, and the block that is
              least important on the page took the most height.

              A rail holds one row whatever the count. Tiles keep a fixed width
              rather than dividing the screen, so a photograph is the same size
              for someone with two trades and someone with six, and the row is
              obviously scrollable because the last tile is cut by the edge.
            */
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.railRow}
              // Hugs its tiles rather than stretching to the page height.
              style={styles.railScroll}
            >
              {booked.map(group => {
                const category = group.category;
                /*
                  The still life, not the professional.

                  `image` is a cutout of a person, drawn to stand out of a
                  plinth on Home. Dropped into a square it is a portrait in a
                  box, and a row of them reads as a list of people rather than
                  a list of trades. `tile` is the same category shot as its
                  tools; it falls back to `image` for anything not shot.
                */
                const art = category.tile ?? category.image;

                return (
                  <Pressable
                    key={category.key}
                    accessibilityRole="button"
                    accessibilityLabel={`Book ${category.label} again`}
                    onPress={() => onSelectCategory?.(category.key)}
                    style={({ pressed }) => [
                      styles.railTile,
                      pressed ? styles.pressed : null,
                    ]}
                  >
                    {art ? (
                      <Image
                        source={art}
                        style={styles.railArt}
                        // The picture is the tile, so it fills its frame.
                        // "contain" would letterbox a photograph inside it.
                        resizeMode="cover"
                      />
                    ) : (
                      /* No artwork shot for this trade yet - its glyph on a
                         tinted square, which reads as "not photographed"
                         rather than as a broken image. */
                      <View style={[styles.railArt, styles.railGlyph]}>
                        <Icon
                          name={category.icon}
                          size={28}
                          color={colors.primary}
                          strokeWidth={1.6}
                        />
                      </View>
                    )}

                    <Text
                      variant="caption"
                      align="center"
                      numberOfLines={2}
                      color={colors.inkBody}
                    >
                      {category.label}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          ) : (
            /* Nothing booked yet, so there is nothing to repeat - the rail
               becomes the one thing worth doing instead of an empty strip. */
            <View style={styles.railEmpty}>
              <Text
                variant="bodySm"
                color={colors.inkMuted}
                style={styles.railFill}
              >
                Nothing booked yet. Your services will show up here.
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Explore services"
                onPress={onBrowse}
                style={({ pressed }) => [
                  styles.explore,
                  pressed ? styles.pressed : null,
                ]}
              >
                <Text variant="labelSm" color={colors.onPrimary}>
                  Explore now
                </Text>
              </Pressable>
            </View>
          )}
        </View>

        {/*
          Between the two sections.

          Inset and rounded rather than full bleed: the artwork reads as a card
          on a pale ground, so running it edge to edge would put a second
          card's worth of margin inside a page that has none.

          ---------------------------------------------------------------------
          THE COPY IS DRAWN, NOT BAKED IN
          ---------------------------------------------------------------------
          `list_banner.png` had its headline, its eyebrow and its four status
          pills flattened into the pixels. That made the words un-translatable,
          un-selectable and un-scalable - they ignored the system font size
          entirely, which is the one setting a partially sighted customer has
          already told the phone about - and it pinned the artwork to one
          language at one size.

          `final.png` is the same scene with the copy left off, so the words
          are real `Text` on top of it. They now translate, respond to font
          scaling, and can be read out by a screen reader in the order they
          appear rather than as one long `accessibilityLabel` on a picture.
        */}
        <ImageBackground
          source={require('../../assets/uploads/booking/final.png')}
          style={[styles.banner, { width: bannerWidth, height: bannerHeight }]}
          imageStyle={styles.bannerImage}
          resizeMode="cover"
        >
          {/*
            Held to the left half. The plate's right side carries the vase and
            the framed print, and text run across it would sit on the busiest
            part of the picture - so the column stops before the shelf begins.
          */}
          <View style={styles.bannerCopy}>
            <Text variant="titleLg" style={styles.bannerTitle}>
              Booking History
            </Text>
            <Text variant="bodySm" color={colors.inkBody}>
              View and manage all your past service bookings.
            </Text>
          </View>
        </ImageBackground>

        {/*
          The list failed to load - said plainly, with the way to try again.

          Not an empty state: "nothing booked yet" in front of somebody with
          six bookings is worse than an error, because they believe it.
        */}
        {error ? (
          <View style={styles.loadError}>
            <Icon name="alert" size={18} color={colors.inkBody} />
            <View style={styles.grow}>
              <Text variant="subtitle">Could not load your bookings</Text>
              <Text variant="caption" color={colors.inkMuted}>
                {error} · Pull down to try again.
              </Text>
            </View>
          </View>
        ) : null}

        {/* ------------------------------------------------------------ cards */}
        <Text variant="titleLg" style={styles.listTitle}>
          Bookings
        </Text>

        {/* Under the heading they filter, not up on the artwork. On the
            banner they read as decoration on a picture; here they are
            plainly the control for the list directly beneath them. */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filters}
          style={styles.filterRail}
        >
          {bookingFilters.map(option => {
            const on = option.key === filter;
            return (
              <Pressable
                key={option.key}
                accessibilityRole="button"
                accessibilityState={{ selected: on }}
                accessibilityLabel={option.label}
                onPress={() => setFilter(option.key)}
                style={[styles.chip, on ? styles.chipOn : null]}
              >
                <Text
                  variant="labelSm"
                  color={on ? colors.onPrimary : colors.inkBody}
                >
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {shown.length ? (
          <View style={styles.group}>
            {shown.map(booking => {
              const tone = TONE[booking.status];
              /*
                The live clock for a job under way, recomputed on each tick of
                `nowTick` below. Null for every other stage, which is what
                leaves the ordinary `eta` copy in place on the strip.
              */
              const live =
                booking.stage === 'service'
                  ? jobProgress(booking.startedAt, booking.duration)
                  : null;
              const trade = (
                categoryFor ??
                ((key: CategoryKey) => seedCategories.find(c => c.key === key))
              )(booking.category)?.label;

              return (
                /*
                 * A ticket: a coloured stub down the left edge, then the card.
                 *
                 * Four lines only - what state it is in, what trade, who is
                 * coming, and what is happening. The tags, the time and the
                 * address all live on the detail page; repeating them here made
                 * a list you had to read rather than scan.
                 */
                <Pressable
                  key={booking.id}
                  accessibilityRole="button"
                  /* The time is on the card now, so it belongs in what a
                     screen reader announces for it too. */
                  accessibilityLabel={[booking.title, tone.label, booking.when]
                    .filter(Boolean)
                    .join(', ')}
                  onPress={() => onOpenBooking?.(booking)}
                  style={({ pressed }) => [
                    styles.ticket,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <View style={[styles.stub, { backgroundColor: tone.fill }]} />

                  <View style={styles.card}>
                    <View style={styles.cardHead}>
                      <View
                        style={[styles.badge, { backgroundColor: tone.fill }]}
                      >
                        <Text variant="eyebrow" color={tone.tint}>
                          {tone.label}
                        </Text>
                      </View>
                      {/*
                        The time, opposite the status chip.

                        "When is it" is the question a live booking is opened
                        to answer, and it was only answerable by tapping in -
                        this row had the badge on one side and nothing but
                        empty space on the other.

                        The price used to sit here too. It moved to the card's
                        foot: a figure is the last thing read on a receipt, and
                        up here it competed with the status for the same
                        glance.
                      */}
                      {booking.when ? (
                        <Text
                          variant="caption"
                          color={colors.inkMuted}
                          numberOfLines={1}
                        >
                          {booking.when}
                        </Text>
                      ) : null}
                    </View>

                    <Text variant="subtitle" numberOfLines={2}>
                      {booking.title}
                    </Text>

                    {trade ? (
                      <Text variant="caption" color={colors.inkMuted}>
                        {trade}
                      </Text>
                    ) : null}

                    {booking.professional ? (
                      <View style={styles.meta}>
                        <Icon name="user" size={14} color={colors.inkMuted} />
                        <Text variant="caption" color={colors.inkMuted}>
                          {booking.professional.name}
                        </Text>
                      </View>
                    ) : null}

                    {/*
                      Where the money got to, on the card rather than only
                      inside the booking.

                      A cancelled row's one remaining question is "and my
                      refund?", and making somebody open the page to find out
                      is what turns a refund into a support ticket. The line
                      distinguishes raised from landed - see
                      `data/refunds.ts` for why those are never one state.
                    */}
                    {refundFor(booking) ? (
                      <View style={styles.meta}>
                        <Icon name="refund" size={14} color={colors.inkMuted} />
                        <Text variant="caption" color={colors.inkMuted}>
                          {refundLine(refundFor(booking)!)}
                        </Text>
                      </View>
                    ) : null}

                    {/* The one line that changes while you are looking at it. */}
                    {booking.progress ? (
                      <View style={styles.progress}>
                        <Icon name="bolt" size={14} color={colors.primary} />
                        <Text
                          variant="caption"
                          color={colors.primary}
                          style={styles.progressText}
                          numberOfLines={1}
                        >
                          {booking.progress}
                        </Text>

                        {/*
                          The estimate, on the same strip as the status it
                          qualifies.

                          Only where the data has one, and what it estimates
                          changes with the stage: travel time while someone is
                          on the way, a finish time once they have arrived. A
                          card with no `eta` - anything scheduled, done or
                          cancelled - simply does not draw it, rather than
                          showing a dash.
                        */}
                        {booking.eta || live ? (
                          <View style={styles.eta}>
                            <Icon
                              name="clock"
                              size={12}
                              color={colors.primary}
                            />
                            <Text variant="caption" color={colors.primary}>
                              {/*
                                While work is happening the card says what is
                                LEFT, not what time it finishes. "Done by
                                12:18 PM" is the same sentence at 10:20 and at
                                12:10, and the whole question being asked of
                                this card mid-job is "how much longer" -
                                `eta` answers it only by arithmetic the reader
                                has to do. Falls back to `eta` the moment the
                                estimate is spent or unreadable, so the strip
                                never empties.
                              */}
                              {live?.remaining ??
                                (live?.overrun ? 'Running over' : booking.eta)}
                            </Text>
                          </View>
                        ) : null}
                      </View>
                    ) : null}

                    {/*
                      Was: how far away the professional is, under the strip.

                      Commented out with the tracking panel on the detail
                      screen - the card cannot claim to know a distance the
                      page behind it has stopped showing, and "2.4 km away" is
                      a position however quietly it is phrased. The ETA above
                      is what this card says about arrival now.

                      See the long note on the `onway` branch in
                      `BookingDetailScreen` before restoring either.

                    {booking.distance ? (
                      <View style={styles.meta}>
                        <Icon name="pin" size={14} color={colors.inkMuted} />
                        <Text variant="caption" color={colors.inkMuted}>
                          {booking.distance}
                        </Text>
                      </View>
                    ) : null}
                    */}

                    {/*
                      Finished work is the most likely thing to want again, and
                      the card already knows which service it was - so the
                      offer belongs here rather than only in the rail above.
                    */}
                    {/*
                      The card's foot: whatever actions it offers, and the
                      price on the right.

                      One row rather than a stack, because most cards carry at
                      most one button - and on a card with none (a live job
                      already under way) the price still needs a home, which is
                      why the row renders regardless and simply pushes the
                      figure to its own edge.
                    */}
                    <View style={styles.cardFoot}>
                      <View style={styles.cardFootActions}>
                    {/*
                      Moving a visit, offered where the plan actually changes.

                      A card is where somebody realises they cannot make
                      Thursday - reaching the picker from here used to mean
                      opening the booking and expanding the "Cancellation &
                      Refund" disclosure, which is the one place a person
                      looking to KEEP their booking will not open. The refund
                      is not what they came for; a different time is.

                      `canReschedule` gates it rather than the status chip:
                      it withdraws the offer once the professional is on their
                      way or working, where there is no future visit to move.
                      Quiet-styled, because the card's filled control is
                      "Book again" and two solid pills would compete.
                    */}
                    {canReschedule(booking) && onReschedule ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={`Reschedule ${booking.title}`}
                        /* Stops the press bubbling to the card, which would
                           open the booking behind the sheet. */
                        onPress={() => onReschedule(booking)}
                        style={({ pressed }) => [
                          styles.move,
                          pressed ? styles.pressed : null,
                        ]}
                      >
                        <Icon
                          name="calendar"
                          size={15}
                          color={colors.inkBody}
                          strokeWidth={2.2}
                        />
                        <Text variant="labelBold" color={colors.inkBody}>
                          Reschedule
                        </Text>
                      </Pressable>
                    ) : null}

                    {booking.status === 'completed' ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={`Book ${booking.title} again`}
                        onPress={() => {
                          /* By id where the booking has one - the two
                             catalogues spell services differently, so the name
                             match below is a fallback, not the plan. */
                          const list = services ?? seedCatalogue;
                          const service =
                            (booking.serviceId
                              ? list.find(
                                  item => item.key === booking.serviceId,
                                )
                              : undefined) ??
                            list.find(
                              item => item.name === booking.services[0],
                            );
                          if (service) {
                            onRebook?.(service);
                          } else {
                            onBrowse();
                          }
                        }}
                        style={({ pressed }) => [
                          styles.again,
                          pressed ? styles.pressed : null,
                        ]}
                      >
                        <Icon
                          name="plus"
                          size={14}
                          color={colors.onPrimary}
                          strokeWidth={2.6}
                        />
                        <Text variant="labelSm" color={colors.onPrimary}>
                          Book again
                        </Text>
                      </Pressable>
                    ) : null}
                      </View>

                      {/* Bottom-right, where a total belongs - and where it is
                          read last rather than competing with the status. */}
                      <Text variant="label">{rupees(booking.price)}</Text>
                    </View>
                  </View>
                </Pressable>
              );
            })}
          </View>
        ) : (
          /* The shared one, not a hand-built panel. This is the honest
             "nothing here" - the filter answered and the answer was none.
             When the list comes from an API, a FAILED request must draw
             `ErrorState` instead: an outage shown as "you have no bookings"
             is how someone books the same job twice. */
          <EmptyState
            icon="calendar"
            title="Nothing here yet"
            message={`You have no ${filter} bookings.`}
            actionLabel="Browse services"
            onAction={onBrowse}
            compact
          />
        )}

        {/*
          ------------------------------------------------------------ support

          Under the list, and under the empty state too - deliberately both.
          "Where is my professional" and "I booked and nothing happened" are
          asked from the same page, and the second one is asked by somebody
          looking at no bookings at all, which is exactly when a help control
          that only renders beside a card would not be there.

          The same button as the one on a booking's own page, and the same
          number behind it. What differs is only what it can say: the detail
          screen knows which booking, this one does not, so the message names
          none rather than guessing at the most recent.
        */}
        <View style={styles.support}>
          <Button
            label="Chat on WhatsApp"
            variant="whatsapp"
            leadingIcon="whatsapp"
            accessibilityLabel="Chat with support on WhatsApp"
            onPress={() =>
              openWhatsappSupport('Hi Homingo, I need help with a booking.')
            }
          />
          <Text
            variant="caption"
            color={colors.inkMuted}
            style={styles.supportNote}
          >
            Need help with a booking? We usually reply in minutes.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  /* Sits above the list rather than replacing it: a stale list plus an honest
     warning beats a blank screen. */
  loadError: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginHorizontal: screenPadding,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surfaceMuted,
  },
  grow: {
    flex: 1,
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  /* Ruled off underneath, so the header reads as the screen's own strip rather
     than as the first thing in the scrolling list. */
  /*
   * Equal air above and below, so the row sits in the middle of its strip.
   *
   * It used to be 16 over 12: with a rule underneath, four points of
   * difference is enough to see, and the title read as sitting low in a band
   * rather than centred in one. Both are `spacing.xl` now - the same number
   * twice, which is the only way this stays centred when the type changes.
   *
   * `gap` is smaller than it was: at 12 the mark and the words were two
   * separate things on one row instead of one label with a mark in front.
   */
  titleBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
    marginBottom: spacing.md,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  /*
   * The weight is spelled out rather than left to the variant: `titleLg` asks
   * for Satoshi-Bold, `assets/fonts/` is empty, so the family resolves to
   * nothing and the text would come back at regular weight.
   */
  title: {
    flex: 1,
    fontWeight: '700',
  },
  count: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },

  /* Held to the page margin by hand: `page` carries no horizontal padding -
     the cards above set their own - so a full-width button would run edge to
     edge and stop reading as a control. */
  support: {
    gap: spacing.sm,
    marginTop: spacing.xl,
    paddingHorizontal: screenPadding,
  },
  supportNote: {
    textAlign: 'center',
  },
  page: {
    // Clears the floating tab bar, which overlays the bottom of the screen.
    // The banner is the last thing on the page now, so it is what would be
    // sitting under the nav rather than a card nobody minds losing an edge of.
    paddingBottom: 120,
  },

  /* No drop of its own now the bell has moved out of its column - the
     `space-between` on the copy block already sets the gap. */
  /* Down from 32. The screen already sits inside a top safe-area inset, so
     that was a second helping of air on top of one the system had provided -
     and at 40pt the headline no longer needs the room to be noticed. */
  /* `flexGrow: 0` so the rail hugs its chips rather than stretching, and the
     margin because nothing else separates them from the first card. Padding
     belongs on the content - on the rail itself it would clip the first
     chip. */
  /* `flexGrow: 0` so the rail hugs its chips, and a margin under it because
     nothing else separates them from the first card - the list's own container
     starts flush. */
  filterRail: {
    flexGrow: 0,
    marginBottom: spacing.md,
  },
  filters: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  chip: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  chipOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },

  /*
   * Pulled up over the banner's foot.
   *
   * The chips sit clear of the banner's lower edge, which left that strip as
   * dead space between them and the first card. It is the stretch the fade has
   * already washed to the page colour, so the list can ride over it and lose
   * nothing - leaving one ordinary gap under the chips instead of two.
   */
  /* Pulled up over the banner's faded foot, the way the list used to be - the
     rail is now the first thing under the chips, so it inherits the trick. */
  /*
   * Pulled up until it meets the illustration itself.
   *
   * The limit is not the fade but the drawing: everything below source y≈578
   * is empty paper, and at this offset the rail lands around y≈669. The wash
   * matters less here than what is underneath it - there is nothing there to
   * cover.
   */
  /* Closed off by a band, so the rail reads as its own section rather than
     running into the archive below it. The same separator the cart uses. */
  /*
   * No negative offset any more.
   *
   * It used to ride up over the banner's faded foot; the banner is below it
   * now, so the same margin would drag the grid off the top of the screen. It
   * opens the page instead, and its own top padding is what sets it off the
   * status bar.
   */
  /*
   * No surface of its own any more.
   *
   * It was white so the grid inside it would not read as part of the
   * illustration below - but the grid has moved into that illustration, and
   * white against the page's green left a hard rule across the screen exactly
   * where the banner's fade was trying to dissolve one. On the page colour
   * there is no seam to hide.
   */
  rail: {
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },
  railTitle: {
    paddingHorizontal: screenPadding,
    // Room under the heading. The tiles are photographs with no card around
    // them now, so nothing else was holding them off the title - and a
    // heading sitting close to full-bleed artwork reads as a caption on it.
    paddingBottom: spacing.xl,
  },
  /*
   * The "Book again" rail.
   *
   * `flexGrow: 0` so it hugs its tiles instead of stretching to fill whatever
   * height is going - a horizontal `ScrollView` inside a vertical one will
   * happily take the rest of the page otherwise.
   *
   * The gutter is on the content, not the rail: on the rail itself it clips
   * the first tile as soon as you scroll, because the padding scrolls with
   * the viewport rather than with what is inside it.
   */
  railScroll: {
    flexGrow: 0,
  },
  railRow: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  /*
   * A fixed width, not a share of the screen.
   *
   * The row is history and has no fixed length, so dividing the width by the
   * count would shrink the photographs the more of the app you use. At 112 two
   * and a half tiles show on the narrowest phone - enough of the third to make
   * it obvious the row moves.
   */
  railTile: {
    width: 112,
    alignItems: 'center',
    gap: spacing.sm,
  },
  railArt: {
    width: 112,
    height: 112,
    borderRadius: radii.md,
  },
  /* Only for a trade with no photograph - see the render. */
  railGlyph: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    ...hairline,
  },
  /* Both dimensions come from the render - see `bannerWidth`. */
  banner: {
    marginHorizontal: screenPadding,
    marginTop: spacing.md,
    /* Space the copy off the plate's edges, and centre it vertically now that
       the headline is the only thing on the banner - `space-between` was
       holding it against the top while a fact strip sat along the foot. */
    padding: spacing.md,
    justifyContent: 'center',
    /* The radius belongs to the bitmap, not the box - see `bannerImage`. A
       radius here alone leaves the artwork's square corners poking out. */
    overflow: 'hidden',
    borderRadius: radii.lg,
  },
  /* `ImageBackground` styles its own inner `Image` separately, so the corners
     have to be rounded on both or the picture squares them off again. */
  bannerImage: {
    borderRadius: radii.lg,
  },
  /* Held to the left of the plate, clear of the vase and the framed print. */
  bannerCopy: {
    maxWidth: '58%',
    gap: spacing.xs,
  },
  bannerTitle: {
    /* The plate is warm cream, so the headline takes the brand green rather
       than the default ink - the same pairing the artwork was drawn around. */
    color: colors.primary,
  },
  /* Two lines held open, so cards with a short name line their prices up with
     the rest rather than floating halfway. */
  railEmpty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginHorizontal: screenPadding,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  railFill: {
    flex: 1,
  },
  explore: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.primary,
  },
  /* Air on both sides: it separates the archive from the rail above it, and
     is not just a caption sitting on the filter chips below. */
  /* No negative offset: the banner is below the list now, so there is no
     faded foot above the heading to ride up over. */
  /* Same drop under the heading as the rail's, so the two sections of the
     page open the same way rather than one looking tighter than the other. */
  listTitle: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
  },

  group: {
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.md,
  },
  /* Stub and card as one clipped block, so the colour reaches the rounded
     corners instead of stopping short of them in a square edge. */
  ticket: {
    flexDirection: 'row',
    marginBottom: spacing.sm,
    borderRadius: radii.md,
    overflow: 'hidden',
    ...hairline,
  },
  stub: {
    width: 5,
  },
  card: {
    flex: 1,
    gap: spacing.xs,
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  /*
   * The card's foot: actions on the left, price on the right.
   *
   * `space-between` with the actions in their own box, so the price sits on
   * the card's edge whether there are two buttons, one, or none - a card with
   * no action still shows its total in the same place as one with two.
   */
  cardFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    /* The gap above the row. The buttons used to carry their own `marginTop`
       and `alignSelf`; inside a flex row both are the row's job, and leaving
       them on would have stacked two margins. */
    marginTop: spacing.sm,
  },
  /* Holds whichever buttons this booking offers. `flexShrink` so a long pair
     yields to the price rather than pushing it off the card. */
  cardFootActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 1,
  },
  /* Badge left, time right - one line, the height of its tallest item. */
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  /* Outlined in the status colour rather than filled with it - filled, it
     competed with the stub, and two solid marks of the same colour on one card
     read as two different things. */
  badge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.sm,
    borderWidth: 1,
  },
  /* Sized to its own words at the left, under the last line - it follows the
     card rather than spanning it, which would make it read as the card's
     primary action instead of an offer. */
  /*
   * Filled in the brand green, not the pale wash.
   *
   * This is the one thing a finished booking is for. Tinted, it sat at the same
   * weight as the status chip and the tags above it and read as another label
   * on the card rather than as the action; solid, it is the only filled mark on
   * the card and there is no question which part is tappable.
   */
  /* The quiet twin of `again` - same shape, outlined instead of filled, so
     the card keeps exactly one solid control. */
  move: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  /*
   * A softened rectangle, not a pill, and sized to its label.
   *
   * `radii.pill` made a fully round capsule - the heaviest shape on a card
   * whose every other surface is a slight radius, so a secondary action read
   * as the loudest thing in the list. `radii.md` is what the rest of the app
   * uses, and the padding came down with it: at `lg`/`sm` the button was
   * nearly as tall as the two lines of text above it.
   */
  again: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radii.md,
    backgroundColor: colors.primary,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  progress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.xs,
    padding: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceAccent,
  },
  /* Takes what is left, so the estimate keeps its width and the status is what
     truncates - the estimate is the shorter and more perishable of the two. */
  progressText: {
    flex: 1,
  },
  /* Its own chip on the strip's own ground, so it reads as a separate fact
     rather than the tail of the sentence beside it. */
  eta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: spacing.sm,
    paddingVertical: 1,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
  },

  empty: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.xxxl,
  },
  browse: {
    marginTop: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default BookingsScreen;
